import { useResizeObserver } from '@ardoq/hooks';
import { s4 } from '@ardoq/design-tokens';
import { HTMLAttributes, Ref, useCallback, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

type TextOverflowEllipsisProps = HTMLAttributes<HTMLDivElement> & {
  onEllipsisChange?: (isTextOverflowed: boolean, innerText: string) => void;
  onInnerTextChange?: (innerText: string) => void;
  lineClamp?: number;
  initialIsTextOverflowed?: boolean;
  focusable?: boolean;
  showWhitespace?: boolean;
  ref?: Ref<HTMLDivElement>;
};

export const WrapperWithEllipsisOverflow = styled.div<{
  $lineClamp?: number;
  $focusable?: boolean;
  $showWhitespace?: boolean;
}>`
  white-space: nowrap;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  /* If focusable, 4px padding is needed to display the focus ring */
  padding: ${props => (props.$focusable ? s4 : 0)};

  ${props =>
    props.$lineClamp &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: ${props.$lineClamp};
      white-space: initial;
      overflow-wrap: break-word;
      -webkit-box-orient: vertical;
    `}

  ${props =>
    props.$showWhitespace &&
    css`
      /** Preserve white space */
      white-space: pre !important;
    `}
`;

/**
 * Wrapper component providing truncation with ellipsis for your content.
 * @component
 */
export const TextOverflowEllipsis = ({
  className,
  children,
  onEllipsisChange,
  onInnerTextChange,
  lineClamp,
  initialIsTextOverflowed,
  focusable,
  showWhitespace,
  ref: forwardedRef,
  ...rest
}: TextOverflowEllipsisProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      ref.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef]
  );
  const isTextOverflowed = useRef(initialIsTextOverflowed ?? false);
  const animationRequestId = useRef<number | null>(0);
  const recalculateOverflow = useCallback(() => {
    if (animationRequestId.current) {
      cancelAnimationFrame(animationRequestId.current);
    }
    animationRequestId.current = requestAnimationFrame(() => {
      animationRequestId.current = null;
      // Recalculate overflow on the next animation frame to ensure DOM measurements are up to date
      const wrapperEl = ref.current;
      if (!wrapperEl || (!onEllipsisChange && !onInnerTextChange)) {
        return;
      }

      const innerText = wrapperEl.textContent ?? '';

      if (onInnerTextChange) {
        onInnerTextChange(innerText);
      }

      if (onEllipsisChange) {
        const { offsetWidth, offsetHeight, scrollWidth, scrollHeight } =
          wrapperEl;
        const currentHasEllipsisActive = lineClamp
          ? scrollHeight - offsetHeight > 9 // For some reason, scrollHeight may be 1-9px larger than offsetHeight when there is no ellipsis
          : offsetWidth < scrollWidth;
        if (currentHasEllipsisActive === isTextOverflowed.current) {
          return;
        }
        isTextOverflowed.current = currentHasEllipsisActive;
        onEllipsisChange(currentHasEllipsisActive, innerText);
      }
    });
  }, [lineClamp, onEllipsisChange, onInnerTextChange]);

  useEffect(recalculateOverflow, [recalculateOverflow]);
  useResizeObserver(ref, recalculateOverflow);

  return (
    <WrapperWithEllipsisOverflow
      ref={setRefs}
      className={className}
      $lineClamp={lineClamp}
      $focusable={focusable}
      $showWhitespace={showWhitespace}
      {...rest}
    >
      {children}
    </WrapperWithEllipsisOverflow>
  );
};
